import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  PlusIcon,
  CalendarIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { fetchJournals } from "../../../api/journal";
import JournalDialog from "../journal/JournalDialog";
import toast from "react-hot-toast";

const isServiceTitle = (title) =>
  typeof title === "string" && /service/i.test(title);

const ToolJournalSection = ({ tool }) => {
  const { t } = useTranslation();
  const [journals, setJournals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showJournalDialog, setShowJournalDialog] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState(null);

  const moduleRecord = useMemo(
    () => (tool?.id ? { id: tool.id, type: "Tool" } : null),
    [tool?.id]
  );

  useEffect(() => {
    if (tool?.id) {
      loadJournals();
    }
  }, [tool?.id]);

  const loadJournals = async () => {
    setIsLoading(true);
    try {
      const response = await fetchJournals({
        module_record_type: "Tool",
        module_record_id: tool.id,
        per_page: 100,
        fields:
          "id,title,description,journal_date,next_follow_up_date,status,input_fields",
      });
      setJournals(response.data || []);
    } catch (error) {
      console.error("Error loading tool journals:", error);
      toast.error(t("Failed to load journal entries"));
    } finally {
      setIsLoading(false);
    }
  };

  const completedServiceJournals = useMemo(
    () =>
      journals.filter(
        (j) => j.status === "completed" && isServiceTitle(j.title)
      ),
    [journals]
  );

  const otherJournals = useMemo(
    () =>
      journals.filter(
        (j) => !(j.status === "completed" && isServiceTitle(j.title))
      ),
    [journals]
  );

  const handleJournalCreated = (newJournal) => {
    setJournals([newJournal, ...journals]);
  };

  const handleJournalUpdated = (updatedJournal) => {
    setJournals(
      journals.map((j) => (j.id === updatedJournal.id ? updatedJournal : j))
    );
  };

  const getJournalIcon = (title) => {
    if (isServiceTitle(title)) {
      return <WrenchScrewdriverIcon className="h-5 w-5 text-blue-600" />;
    }
    return <DocumentTextIcon className="h-5 w-5 text-gray-600" />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "archived":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const renderJournalCard = (journal) => (
    <div
      key={journal.id}
      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
    >
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-1">
          {getJournalIcon(journal.title)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              {journal.title}
            </h4>
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                journal.status
              )}`}
            >
              {t(`journal.status.${journal.status}`)}
            </span>
          </div>

          <p className="text-sm text-gray-700 mb-3">{journal.description}</p>

          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center">
              <CalendarIcon className="h-4 w-4 mr-1" />
              {formatDate(journal.journal_date)}
            </div>
            {journal.next_follow_up_date && (
              <div className="flex items-center">
                <span className="mr-1">{t("Next follow-up:")}</span>
                {formatDate(journal.next_follow_up_date)}
              </div>
            )}
          </div>

          {journal.input_fields && journal.input_fields.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-2">
                {journal.input_fields.map((field, index) => (
                  <div key={index} className="text-xs">
                    <span className="font-medium text-gray-700">
                      {t(field.name)}:
                    </span>{" "}
                    <span className="text-gray-600">{field.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => {
                setSelectedJournal(journal);
                setShowJournalDialog(true);
              }}
              className="text-sm text-blue-600 hover:text-blue-900"
            >
              {t("Edit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!tool?.id) return null;

  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="px-4 py-5 sm:p-6 space-y-8">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {t("tool.completed_service_intervals")}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {t("tool.completed_service_intervals_hint")}
          </p>
          {isLoading ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            </div>
          ) : completedServiceJournals.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">
              {t("tool.no_completed_service_intervals")}
            </p>
          ) : (
            <div className="space-y-3">
              {completedServiceJournals.map((journal) =>
                renderJournalCard(journal)
              )}
            </div>
          )}
        </div>

        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {t("journal.entries")}
              </h3>
              <p className="text-sm text-gray-500">
                {t("tool.journal_other_hint")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedJournal(null);
                setShowJournalDialog(true);
              }}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {t("journal.add_entry")}
            </button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            </div>
          ) : otherJournals.length === 0 && journals.length === 0 ? (
            <div className="text-center py-8">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {t("journal.no_entries")}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t("tool.journal_empty_hint")}
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowJournalDialog(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t("journal.add_entry")}
                </button>
              </div>
            </div>
          ) : otherJournals.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">
              {t("tool.journal_no_other_entries")}
            </p>
          ) : (
            <div className="space-y-4">
              {otherJournals.map((journal) => renderJournalCard(journal))}
            </div>
          )}
        </div>
      </div>

      <JournalDialog
        open={showJournalDialog}
        onClose={() => {
          setShowJournalDialog(false);
          setSelectedJournal(null);
        }}
        moduleType="tool"
        moduleParam={tool?.id?.toString()}
        moduleRecord={moduleRecord}
        journal={selectedJournal}
        onJournalCreated={handleJournalCreated}
        onJournalUpdated={handleJournalUpdated}
      />
    </div>
  );
};

export default ToolJournalSection;
